import {
  GetProductById,
  GetProductsByBatch,
  GetVariantById,
} from '@nova/queries'
import { isStringifiedArray } from '@nova/utils'
import '@vendor/storefront-client'

const registerGlobal = window.DNARegisterGlobal

/**
 * A class leveraging Shopify's 1st party storefront-api-client for requesting data from Storefront API
 */
class StorefrontClient {
  /**
   * @param {string} domain - The domain of the Shopify store.
   * @param {string} accessToken - The access token for the Storefront API.
   * @param {string} apiVersion - The API version to use.
   */
  constructor(
    storeDomain = __NOVA_GLOBAL__.domain,
    apiVersion = '2025-07',
    publicAccessToken = __NOVA_GLOBAL__.storefrontToken,
  ) {
    this.client = ShopifyStorefrontAPIClient.createStorefrontApiClient({
      storeDomain,
      apiVersion,
      publicAccessToken,
    })
  }

  /**
   * Fetch product data by its ID.
   * @param {string} id - The product ID.
   * @returns {Promise<ClientResponse<TData>>} A promise to return GraphQL data
   */
  async getProductById(id) {
    const response = await this.client.request(GetProductById, {
      variables: { id: `gid://shopify/Product/${id}` },
    })
    return cleanUpProductData(response?.data?.node)
  }

  /**
   * Fetch variant data by its ID.
   * @param {string} id - The variant ID.
   * @returns {Promise<ClientResponse<TData>>} A promise to return GraphQL data
   */
  async getVariantById(id) {
    const response = await this.client.request(GetVariantById, {
      variables: { id: `gid://shopify/ProductVariant/${id}` },
    })
    return cleanUpVariantData(response?.data?.node)
  }

  /**
   * Fetch batch products data by IDs.
   * @param {Array} ids - Product IDs.
   * @returns {Promise<ClientResponse<TData>>} A promise to return GraphQL data
   */
  async getProductsByBatch(ids) {
    const structuredIds = []
    for (let i = 0; i < ids.length; i++) {
      structuredIds.push(`gid://shopify/Product/${ids[i]}`)
    }

    const response = await this.client.request(GetProductsByBatch, {
      variables: { ids: structuredIds },
    })

    return cleanUpProductsData(response?.data)
  }
}

/**
 * GraphQL returns numbers in dollars and as a string. This function safely converts it
 * to a number object in cents.
 * @param {string|number} price - The price value.
 * @returns {number}
 */
const normalizePrice = (price) => {
  if ([null, undefined].includes(price)) return null
  return Number(price) * 100
}

/**
 * Strips GraphQL Global ID (GID) down to its numeric component.
 * @param {string} value - The GID.
 * @returns {string} The stripped GID.
 */
function stripGID(value) {
  const gidPattern = /^gid:\/\/shopify\/\w+\//
  if (gidPattern.test(value)) {
    return value.replace(gidPattern, '')
  }
  return value
}

/**
 * Makes metafield data from GraphQL workable with JS.
 * @param {Array<Object>} rawMetafields - The GraphQL response for metafields.
 * @returns {Object} The processed metafield data.
 */
function cleanUpMetafields(rawMetafields) {
  if (!rawMetafields) return {}
  return rawMetafields
    .filter((m) => m !== null)
    .reduce((acc, metafield) => {
      if (!acc[metafield.namespace]) {
        acc[metafield.namespace] = {}
      }
      let value
      if (Object.keys(metafield.reference || {}).includes('image')) {
        value = metafield.reference.image.originalSrc
      } else if (Object.keys(metafield.reference || {}).includes('url')) {
        value = metafield.reference.url
      } else if (isStringifiedArray(metafield.value)) {
        const array = JSON.parse(metafield.value)
        value = array.map((i) => stripGID(i))
      } else {
        value = stripGID(metafield.value)
      }
      acc[metafield.namespace][metafield.key] = value
      return acc
    }, {})
}

/**
 * Cleans up the metaobjects response data and extracts relevant information.
 *
 * @param {Array} metaobjects - The array of metaobject edges from the GraphQL response.
 * @returns {Array} An array of cleaned-up metaobject objects.
 */
function cleanupMetaobjects(metaobjects) {
  if (!Array.isArray(metaobjects)) {
    console.warn('Invalid metaobjects data. Expected an array.')
    return []
  }

  return metaobjects
    .map((edge) => {
      if (!edge || !edge.node) {
        console.warn('Invalid metaobject edge. Skipping.')
        return null
      }

      const { node } = edge
      const { id, handle, type, updatedAt, onlineStoreUrl, fields } = node

      const cleanedFields = Array.isArray(fields)
        ? fields.reduce((acc, field) => {
            if (!field || !field.key) {
              console.warn('Invalid field data. Skipping.')
              return acc
            }
            acc[field.key] = field.value
            return acc
          }, {})
        : {}

      return {
        id: stripGID(id),
        handle,
        type,
        updatedAt,
        onlineStoreUrl,
        fields: cleanedFields,
      }
    })
    .filter(Boolean)
}

/**
 * Makes product data from GraphQL workable with JS.
 * @param {Object} rawProduct - The GraphQL response for product.
 * @returns {Object|null} The processed product data.
 */
function cleanUpProductData(rawProduct) {
  if (!rawProduct) {
    return null
  }

  const variantsSortedByPrice =
    rawProduct.variants?.edges?.sort((a, b) => {
      const priceA = Number.parseFloat(a.node.priceV2.amount)
      const priceB = Number.parseFloat(a.node.priceV2.amount)
      if (priceA < priceB) return -1
      if (priceA > priceB) return 1
      return 0
    }) || []
  const lowestPriceVariant = variantsSortedByPrice[0]
  let compareAtPrice = null
  if (variantsSortedByPrice.length) {
    const compareAtPriceProperty =
      lowestPriceVariant.node.compareAtPriceV2?.amount
    if (compareAtPriceProperty)
      compareAtPrice = Number.parseFloat(compareAtPriceProperty)
  }

  return {
    id: stripGID(rawProduct.id),
    available: rawProduct.availableForSale,
    title: rawProduct.title,
    description: rawProduct.descriptionHtml,
    minPrice: normalizePrice(rawProduct.priceRange?.minVariantPrice?.amount),
    maxPrice: normalizePrice(rawProduct.priceRange?.maxVariantPrice?.amount),
    compareAtPrice: normalizePrice(compareAtPrice),
    handle: rawProduct.handle,
    type: rawProduct.productType,
    vendor: rawProduct.vendor,
    createdAt: new Date(rawProduct.createdAt),
    updatedAt: new Date(rawProduct.updatedAt),
    publishedAt: new Date(rawProduct.publishedAt),
    onlineStoreUrl: rawProduct.onlineStoreUrl,
    tags: rawProduct.tags,
    media: rawProduct?.media?.edges.map((edge) => {
      return cleanUpMediaData(edge.node)
    }),
    options: rawProduct.options,
    collections: rawProduct.collections?.edges.map((edge) => {
      return cleanUpCollectionData(edge)
    }),
    variants: rawProduct.variants?.edges.map((edge) => {
      return cleanUpVariantData(edge.node)
    }),
    metafields: cleanUpMetafields(rawProduct.metafields),
    isDiscounted: rawProduct.variants?.edges.some((variantEdge) => {
      const variant = variantEdge.node
      const originalPrice = Number.parseFloat(variant.priceV2.amount)
      const compareAtPrice = Number.parseFloat(variant.compareAtPriceV2?.amount)
      return compareAtPrice > originalPrice
    }),
  }
}

/**
 * Makes batch products data from GraphQL workable with JS.
 * @param {Array} rawProducts - The GraphQL response for batch products.
 * @returns {Array|null} The processed product data.
 */
function cleanUpProductsData(rawData) {
  if (!rawData) {
    return null
  }
  return rawData.nodes.map((product) => cleanUpProductData(product))
}

/**
 * Makes media data from GraphQL workable with JS.
 * @param {Object} rawVariant - The GraphQL response for media.
 * @returns {Object|null} The processed media data.
 */
function cleanUpMediaData(rawMedia) {
  if (!rawMedia) {
    return null
  }
  const type = rawMedia?.mediaContentType.toLowerCase()
  switch (type) {
    case 'image':
      return {
        type,
        url: rawMedia?.image?.url,
        alt: rawMedia?.alt,
      }
    case 'video':
      return {
        type,
        url: rawMedia?.sources[0]?.url,
        alt: rawMedia?.alt,
        poster: rawMedia?.previewImage?.url,
      }
  }
}

/**
 * Makes variant data from GraphQL workable with JS.
 * @param {Object} rawVariant - The GraphQL response for variant.
 * @returns {Object|null} The processed variant data.
 */
function cleanUpVariantData(rawVariant) {
  if (!rawVariant) {
    return null
  }
  return {
    id: stripGID(rawVariant?.id),
    title: rawVariant?.title,
    price: normalizePrice(rawVariant?.priceV2?.amount),
    compareAtPrice: normalizePrice(rawVariant?.compareAtPriceV2?.amount),
    currencyCode: rawVariant?.priceV2?.currencyCode,
    available: rawVariant?.availableForSale,
    sku: rawVariant?.sku,
    selectedOptions: rawVariant?.selectedOptions,
    quantityAvailable: rawVariant?.quantityAvailable,
    currentlyNotInStock: rawVariant?.currentlyNotInStock,
    metafields: cleanUpMetafields(rawVariant?.metafields),
  }
}

/**
 * Process the search data response.
 * @param {Object} rawData - The raw search data.
 * @returns {Array<Object>} The list of processed product data.
 */
function cleanUpSearchData(rawData) {
  if (!rawData) {
    return null
  }
  return rawData.products.edges.map((product) => cleanUpProductData(product))
}

/**
 * Makes collection data from GraphQL workable with JS.
 * @param {Object} rawData - The GraphQL response for collection.
 * @returns {Object|null} The processed collection data.
 */
function cleanUpCollectionData(rawData) {
  if (!rawData || !rawData.node) {
    return null
  }

  const rawCollection = rawData.node

  return {
    id: stripGID(rawCollection.id),
    title: rawCollection.title,
    handle: rawCollection.handle,
    description: rawCollection.descriptionHtml,
    updatedAt: new Date(rawCollection.updatedAt),
    publishedAt: new Date(rawCollection.publishedAt),
    image: rawCollection.image,
    products: rawCollection.products?.edges.map((edge) => {
      return cleanUpProductData(edge)
    }),
    metafields: cleanUpMetafields(rawCollection.metafields),
  }
}

/**
 * Maps an array of metaobjects to an object where the keys are the "handle" values.
 *
 * @param {Array} metaobjects - An array of metaobject objects.
 * @returns {Object} An object where the keys are the "handle" values and the values are the corresponding metaobject properties (excluding "handle").
 */
export function mapMetaobjectsByHandle(metaobjects) {
  if (!Array.isArray(metaobjects)) {
    console.warn('Invalid metaobjects data. Expected an array.')
    return {}
  }

  return metaobjects.reduce((acc, metaobject) => {
    if (!metaobject || !metaobject.handle) {
      console.warn('Invalid metaobject data. Skipping.')
      return acc
    }

    const { handle, ...rest } = metaobject
    acc[handle] = rest
    return acc
  }, {})
}

// Export singleton for client class to prevent need to instantiate each time
export const storefrontClient = new StorefrontClient()
